import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

// Account types based on Rails model
export interface Account {
  id: string;
  name: string;
  account_manager_name: string | null;
  account_manager_email: string | null;
  account_manager_phone: string | null;
  account_manager_tz: string | null;
  notes: string | null;
  salesforce_sfid: string | null;
  registration_slug: string | null;
  uses_saml_login: boolean;
  company_email_domain: string | null;
  license_domains: string | null;
  // Computed fields
  total_users: number;
  total_products: number;
  has_active_licenses: boolean;
  license_admins: string[];
  licenses?: License[];
}

export interface License {
  id: string;
  product_id: string;
  product_name: string;
  product_code: string;
  is_legacy: boolean;
  active: boolean;
  usage_end_date: string | null;
  seats_purchased: number;
  seats_comped: number;
  total_seats: number;
  seats_used: number;
  seats_available: number;
  allow_auto_assignment: boolean;
}

export interface AccountListParams {
  q?: string;                      // Search query
  active_licenses_only?: boolean;  // Filter for accounts with active licenses
  limit?: number;
  skip?: number;
}

// Account service for managing accounts
export const accountService = {
  // List all accounts with filtering and pagination
  async listAccounts(params?: AccountListParams): Promise<{
    data: JsonApiResource<Account>[];
    meta: {
      total_count: number;
      limit: number;
      skip: number;
    };
  }> {
    const queryParams: Record<string, any> = {};
    
    if (params?.q) queryParams.q = params.q;
    if (params?.active_licenses_only) queryParams.active_licenses_only = 'true';
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.skip) queryParams.skip = params.skip;
    
    return apiClient.get('/api/admin/accounts', queryParams);
  },

  // Get specific account with license details
  async getAccount(id: string): Promise<ApiResponse<JsonApiResource<Account>>> {
    return apiClient.get(`/api/admin/accounts/${id}`);
  },
};